import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
    Box,
    Grid,
    Tooltip
} from '@mui/material';
import {
    CreditCard,
    Image,
    Business,
    Home,
    Info
} from '@mui/icons-material';

import {
    useChangeable,
    FullForm,
    TextField,
    EmailField,
    PhoneField,
    WebsiteField,
    NumberField
} from '@commonComponents/Form';
import { SectionDivider } from '@commonComponents';
import { cleanEntityMetadata } from '@utils/metadata';
import { useDispatch, useSelector } from '@hooks';
import { updateTenantProfile } from '@api/tenants';
import toast from '@utils/toast';
import { fetchTenantProfile, selectFetchingTenantProfile, selectTenantProfile } from '@actions/tenants';
import { fetchSettableMetadataForTenant } from '@actions/metadata';
import { selectUserProfile } from '@actions/users';

import Location from './Form/Location';
import Metadata from './Form/Metadata';

import LogotypeHandler from './TenantProfileComponents/LogotypeHandler';

const defaultTenantProfile = {
    email: '',
    phone: '',
    website: '',
    billingEmail: '',
    billingMark: '',
    billingStreet: '',
    billingZip: '',
    billingCity: '',
    city: '',
    street: '',
    zip: '',
    lat: null,
    lng: null,
    vehicleTypes: [] as string[],
    equipment: [] as string[],
    licencesCertificates: [] as string[],
    customLicencesCertificates: [] as string[],
    sustainability: [] as string[],
    numberOfTrucksOverride: null as number | null
};

const TenantProfileForm: React.FC = () => {
    const dispatch = useDispatch();
    const tenantProfile = useSelector(selectTenantProfile());
    const fetchingTenantProfile = useSelector(selectFetchingTenantProfile());
    const userProfile = useSelector(selectUserProfile());
    const [entity, onChange] = useChangeable(defaultTenantProfile);
    const navigate = useNavigate();

    const onSubmit = async () => {
        // Routes are managed on /routes; omitting them leaves them untouched
        const profile = { ...entity };
        delete profile.routes;
        const cleanedEntity = cleanEntityMetadata(profile);
        try {
            await updateTenantProfile(cleanedEntity);
            await dispatch(fetchTenantProfile());
            toast('Företagsuppgifter uppdaterade');
            navigate(-1);
        } catch (e: any) {
            toast(e, 'Kunde inte uppdatera företagsuppgifterna, försök igen');
        }
    };

    React.useEffect(() => {
        dispatch(fetchTenantProfile());
        dispatch(fetchSettableMetadataForTenant());
    }, [dispatch]);

    React.useEffect(() => {
        if (!tenantProfile || fetchingTenantProfile) return;
        onChange(tenantProfile);
    }, [fetchingTenantProfile, tenantProfile, onChange]);

    return (
        <FullForm
            title="Redigera företagsuppgifter"
            entity={entity}
            onSubmit={onSubmit}
            showBottomSave
        >
            <Grid container spacing={2}>
                <Grid item xs={12} md={6} lg={4}>
                    <SectionDivider
                        label="Logotyp"
                        icon={<Image fontSize="small" />}
                        marginTop={1}
                        sx={{ cursor: 'pointer' }}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            width: '100%',
                            maxWidth: { xs: '100%', md: 280 },
                            mx: 'auto',
                            py: { xs: 2, md: 7 }
                        }}
                    >
                        <LogotypeHandler
                            url={entity.logoUrl}
                            name={entity.name}
                            size={100}
                            showButton
                            onUploaded={async () => {
                                dispatch(fetchTenantProfile());
                            }}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} md={6} lg={8}>
                    <SectionDivider
                        label="Företagsuppgifter"
                        icon={<Business fontSize="small" />}
                        marginTop={1}
                        sx={{ cursor: 'pointer' }}
                    />
                    <Grid container spacing={2}>
                        <Tooltip title="Kontakta oss om du behöver ändra namn." placement="bottom-start">
                            <Grid item xs={12}>
                                <TextField
                                    name="name"
                                    label="Namn"
                                    value={entity.name}
                                    onChange={onChange}
                                    disabled
                                />
                            </Grid>
                        </Tooltip>
                        <Tooltip title="Kontakta oss om du behöver ändra org. nummer." placement="bottom-start">
                            <Grid item xs={12}>
                                <TextField
                                    name="orgNumber"
                                    label="Org. nummer"
                                    value={entity.orgNumber}
                                    onChange={onChange}
                                    disabled
                                />
                            </Grid>
                        </Tooltip>
                        <Grid item xs={12}>
                            <EmailField
                                name="email"
                                label="E-post"
                                value={entity.email}
                                onChange={onChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <PhoneField
                                name="phone"
                                label="Telefon"
                                landlineAllowed
                                value={entity.phone}
                                onChange={onChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <WebsiteField
                                name="website"
                                label="Hemsida"
                                value={entity.website}
                                onChange={onChange}
                            />
                        </Grid>
                        {userProfile?.isAdmin && entity.isCarrier && (
                            <Grid item xs={12}>
                                <NumberField
                                    name="numberOfTrucksOverride"
                                    label="Antal lastbilar"
                                    value={entity.numberOfTrucksOverride?.toString() || ''}
                                    onChange={onChange}
                                    minValue={0}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={6}>
                            <SectionDivider
                                label="Om företaget"
                                icon={<Info fontSize="small" />}
                                marginTop={1}
                                sx={{ cursor: 'pointer' }}
                            />
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        name="description"
                                        label="Presentationstext"
                                        helperText="Berätta om ert företag, era kvalifikationer och kompetenser."
                                        value={entity.description || ''}
                                        onChange={onChange}
                                        multiline
                                        minRows={5}
                                        maxLength={599}
                                    />
                                </Grid>
                            </Grid>

                            <SectionDivider
                                label="Faktureringsuppgifter"
                                icon={<CreditCard fontSize="small" />}
                                marginTop={1}
                                sx={{ cursor: 'pointer', mt: 4 }}
                            />
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <EmailField
                                        name="billingEmail"
                                        label="E-post för faktura"
                                        value={entity.billingEmail}
                                        onChange={onChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        name="billingMark"
                                        label="Märkning"
                                        value={entity.billingMark}
                                        onChange={onChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        name="billingStreet"
                                        label="Gata"
                                        value={entity.billingStreet}
                                        onChange={onChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        name="billingZip"
                                        label="Postnummer"
                                        value={entity.billingZip}
                                        onChange={onChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        name="billingCity"
                                        label="Postort"
                                        value={entity.billingCity}
                                        onChange={onChange}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <SectionDivider
                                label="Besöksadress"
                                icon={<Home fontSize="small" />}
                                marginTop={1}
                                sx={{ cursor: 'pointer' }}
                            />
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Location
                                        entity={entity}
                                        onChange={onChange}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Metadata
                    entity={entity}
                    onChange={onChange}
                />
            </Grid>
        </FullForm>
    );
};

export default TenantProfileForm;
